import { useEffect, useState } from 'react'
import {
  Table,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Button,
  Input,
  Row,
  Col,
  Label
} from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import { useParams } from 'react-router-dom'

const ViewPackList = () => {
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null

  const [packListData, setPackListData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { sonumber } = useParams()

  // initialize to non-null default to avoid reactstrap warning
  const [openId, setOpenId] = useState('')

  // Edit mode state
  const [editingPackListId, setEditingPackListId] = useState(null)
  const [editedLineItems, setEditedLineItems] = useState({})
  const [editedBoxNumber, setEditedBoxNumber] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState(null)

  // Store state for editing
  const [stores, setStores] = useState([])
  const [loadingStores, setLoadingStores] = useState(false)
  const [selectedStore, setSelectedStore] = useState('')

  useEffect(() => {
    const fetchPackLists = async () => {
      setLoading(true)
      setError(null)
      try {
        const url = `${API_URL}pick-lists?pagination=false&search=${sonumber}`
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : ''
          }
        })

        if (!res.ok) {
          const txt = await res.text()
          throw new Error(txt || `Status ${res.status}`)
        }

        const json = await res.json()

        // tolerate different response shapes
        const docs =
          json?.message?.docs ||
          json?.data?.docs ||
          json?.docs ||
          (Array.isArray(json) ? json : []) ||
          []

        setPackListData(docs)
      } catch (err) {
        console.error('Fetch pick-lists error:', err)
        setError(err.message || 'Failed to fetch pack lists')
        setPackListData([])
      } finally {
        setLoading(false)
      }
    }

    fetchPackLists()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch stores for edit mode
  useEffect(() => {
    const getStoreData = async () => {
      try {
        setLoadingStores(true)
        const res = await fetch(`${API_URL}stores`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const json = await res.json()
        setStores(json?.data?.docs || [])
      } catch (err) {
        console.error('Error fetching stores:', err)
        setStores([])
      } finally {
        setLoadingStores(false)
      }
    }
    if (token) getStoreData()
  }, [token])

  const toggleAccordion = id => {
    setOpenId(prev => (prev === id ? '' : id))
  }

  // Start editing a rejected packlist
  const handleStartEdit = (pl) => {
    const plId = pl._id ?? pl.id
    setEditingPackListId(plId)
    setEditedBoxNumber(pl.boxNumber ?? '')
    // Initialize selected store from packlist
    const storeId = pl?.store?._id ?? pl?.store ?? ''
    setSelectedStore(storeId)

    // Initialize edited line items from packlist
    const lineItems = Array.isArray(pl.lineItems) ? pl.lineItems : []
    const editedItems = {}
    lineItems.forEach((li, idx) => {
      const lineId = li._id ?? `line-${idx}`
      editedItems[lineId] = {
        _id: li._id,
        model: li.model?._id ?? li.model,
        quantity: li.quantity ?? 0,
        listPrice: li.listPrice ?? 0,
        discount: li.discount ?? 0,
        rate: li.rate ?? 0,
        gst: li.gst ?? 18,
        narration: li.narration ?? '',
        quotationPrice: li.quotationPrice ?? li.rate ?? 0,
        standardLeadTime: li.standardLeadTime,
        committedDeliveryDate: li.committedDeliveryDate
      }
    })
    setEditedLineItems(editedItems)
    setMessage(null)
  }

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPackListId(null)
    setEditedLineItems({})
    setEditedBoxNumber('')
    setSelectedStore('')
    setMessage(null)
  }

  // Handle field changes for editing
  const handleEditFieldChange = (lineId, field, value) => {
    setEditedLineItems(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        [field]: value
      }
    }))
  }

  // Calculate discount from quote price
  const calculateDiscountFromQuote = (sellPrice, quotePrice) => {
    const sp = Number(sellPrice || 0)
    const qp = Number(quotePrice || 0)
    if (qp <= 0 || sp <= 0 || qp <= sp) return 0
    return Number((((qp - sp) / qp) * 100).toFixed(2))
  }

  // Resubmit the edited packlist
  const handleResubmit = async (plId) => {
    setMessage(null)
    setSubmitting(true)

    try {
      // Build the updated line items
      const updatedLineItems = Object.values(editedLineItems).map(li => {
        const calculatedDiscount = calculateDiscountFromQuote(
          li.rate,
          li.quotationPrice
        )
        return {
          _id: li._id,
          model: li.model,
          quantity: Number(li.quantity),
          listPrice: Number(li.listPrice),
          discount: calculatedDiscount,
          rate: Number(li.rate),
          gst: Number(li.gst),
          narration: li.narration || '',
          quotationPrice: Number(li.quotationPrice),
          standardLeadTime: li.standardLeadTime,
          committedDeliveryDate: li.committedDeliveryDate,
          value: Number(li.rate) * Number(li.quantity)
        }
      })

      // Validate at least one item with quantity > 0
      if (!updatedLineItems.some(li => li.quantity > 0)) {
        setMessage({ type: 'error', text: 'At least one line item must have quantity > 0.' })
        setSubmitting(false)
        return
      }

      // Validate store selection
      if (!selectedStore) {
        setMessage({ type: 'error', text: 'Please select a store.' })
        setSubmitting(false)
        return
      }

      const payload = {
        boxNumber: editedBoxNumber,
        store: selectedStore,
        lineItems: updatedLineItems,
        approvalStatus: 'pending' // Change status from rejected to pending
      }

      const res = await fetch(`${API_URL}pick-lists/${plId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })

      const result = await res.json()

      if (!res.ok || result.error === true) {
        throw new Error(result.message || 'Failed to update pack list')
      }

      // 🔽 After successful resubmit, adjust DI quantities
      try {
        // Find the packlist to get DI reference
        const packList = packListData.find(
          pl => (pl._id ?? pl.id) === plId
        )

        if (packList && packList.deliveryInstruction) {
          const diId =
            packList.deliveryInstruction._id ??
            packList.deliveryInstruction.id ??
            packList.deliveryInstruction

          // Fetch the current DI
          const diRes = await fetch(
            `${API_URL}delivery-instructions/${diId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              }
            }
          )

          if (diRes.ok) {
            const diJson = await diRes.json()
            const currentDI = diJson?.message

            // 🔽 Build model → quantity map from NEW resubmitted packlist
            // Note: When the packlist was rejected, its quantities were already added back to DI
            // So we just need to subtract the new quantities from the current DI state
            const modelSubtract = {}
            updatedLineItems.forEach(li => {
              modelSubtract[String(li.model)] =
                (modelSubtract[String(li.model)] || 0) + Number(li.quantity)
            })

            // 🔽 Update DI line items by subtracting the NEW quantities
            // Note: When the packlist was rejected, its quantities were already added back to DI
            // So we just need to subtract the new quantities from the current DI state
            const updatedDILineItems = (
              Array.isArray(currentDI?.lineItems)
                ? currentDI.lineItems
                : []
            ).map(li => {
              const modelId =
                li?.model?._id ??
                (typeof li?.model === 'string' ? li.model : null)

              const currentQty = Number(li?.quantity ?? 0)
              const newQty = modelId ? modelSubtract[String(modelId)] || 0 : 0

              // Calculate: current - new
              // The DI already has the original quantities restored (from rejection)
              // We simply subtract the new packlist quantities
              const finalQty = Math.max(0, currentQty - newQty)

              // ✅ Build the update payload
              // CRITICAL: Send only model ID to backend, it will populate the full model object
              return {
                _id: li._id,
                model: modelId, // Send only the ID, backend will populate the full object
                quantity: finalQty,
                listPrice: li.listPrice,
                discount: li.discount,
                rate: li.rate,
                standardLeadTime: li.standardLeadTime,
                committedDeliveryDate: li.committedDeliveryDate
              }
            })

            // PATCH the DI with updated quantities
            const diPatchRes = await fetch(
              `${API_URL}delivery-instructions/${diId}`,
              {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ lineItems: updatedDILineItems })
              }
            )

            if (!diPatchRes.ok) {
              console.error(
                'Failed to adjust DI quantities:',
                await diPatchRes.text()
              )
              setMessage({
                type: 'warning',
                text: 'Pack list resubmitted, but failed to adjust Delivery Instruction quantities.'
              })
              setSubmitting(false)
              return
            }
          }
        }
      } catch (err) {
        console.error('Error adjusting DI quantities:', err)
        setMessage({
          type: 'warning',
          text: 'Pack list resubmitted, but error occurred while adjusting Delivery Instruction quantities.'
        })
        setSubmitting(false)
        return
      }

      setMessage({ type: 'success', text: 'Pack list resubmitted successfully! Status changed to Pending and DI quantities adjusted.' })

      // Reset edit state
      setEditingPackListId(null)
      setEditedLineItems({})
      setEditedBoxNumber('')
      setSelectedStore('')

      // Reload to reflect changes
      setTimeout(() => {
        window.location.reload()
      }, 1500)

    } catch (err) {
      console.error('Resubmit error:', err)
      setMessage({ type: 'error', text: err.message || 'Failed to resubmit pack list' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4 text-center'>Loading pack lists...</CardBody>
      </div>
    )
  }

  if (error) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4'>
          <div className='alert alert-danger'>Error: {error}</div>
        </CardBody>
      </div>
    )
  }

  if (!packListData || packListData.length === 0) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4 text-center'>No pack lists found.</CardBody>
      </div>
    )
  }

  return (
    <div className='card shadow-sm'>
      <CardBody className='p-4'>
        <h5 className='mb-4'>Pack Lists</h5>

        {message && (
          <div
            className={`alert ${message.type === 'success'
              ? 'alert-success'
              : message.type === 'error'
                ? 'alert-danger'
                : 'alert-info'
              }`}
            role='alert'
          >
            {message.text}
          </div>
        )}

        <Accordion
          open={openId || ''}
          toggle={toggleAccordion}
          id='viewPacklistAccordion'
        >
          {packListData.map((pl, idx) => {
            const id = String(pl._id ?? pl.id ?? `pl-${idx}`)
            const plId = pl._id ?? pl.id
            const isRejected = pl?.approvalStatus === 'rejected'
            const isEditing = editingPackListId === plId

            const boxNumber = pl.boxNumber ?? pl.box ?? '-'
            const salesOrderNumber = pl?.salesOrderNumber ?? '-'
            const diNumber =
              pl?.deliveryInstruction?.deliveryInstructionId ?? '-'
            const rejectionReason = pl?.rejectionReason ?? ''

            // Build pricing map from salesOrder -> lineItems for rate/discount/listPrice if available
            const pricingMap = new Map()
            if (Array.isArray(pl?.salesOrder?.lineItems)) {
              pl.salesOrder.lineItems.forEach(li => {
                // key by model id string (li.model may be id or object)
                const key =
                  li?.model?._id ??
                  (typeof li.model === 'string' ? li.model : null)
                if (key) pricingMap.set(String(key), li)
              })
            }

            const lineItems = Array.isArray(pl.lineItems)
              ? pl.lineItems
              : Array.isArray(pl?.deliveryInstruction?.lineItems)
                ? pl.deliveryInstruction.lineItems
                : []

            return (
              <AccordionItem key={id}>
                <AccordionHeader targetId={id}>
                  <div className='d-flex flex-column flex-md-row align-items-start gap-2 w-100'>
                    <div style={{ minWidth: 220 }}>
                      <strong>
                        {salesOrderNumber} | {diNumber} |{' '}
                        <span className='text-muted small'>
                          Box: {boxNumber}
                        </span>{' '}
                        |{' '}
                        <span
                          className={
                            pl?.approvalStatus === 'approved'
                              ? 'text-success'
                              : pl?.approvalStatus === 'rejected'
                                ? 'text-danger'
                                : 'text-primary'
                          }
                        >
                          {pl?.approvalStatus?.toUpperCase()}
                        </span>
                      </strong>
                    </div>
                  </div>
                </AccordionHeader>

                <AccordionBody accordionId={id}>
                  {/* Show rejection reason if rejected */}
                  {isRejected && rejectionReason && (
                    <div className='alert alert-warning mb-3'>
                      <strong>Rejection Reason:</strong> {rejectionReason}
                    </div>
                  )}



                  {/* Editable store and box number when editing */}
                  {isEditing && (
                    <Row className='g-3 mb-3'>
                      <Col md={4}>
                        <Label className='form-label fw-semibold'>Store</Label>
                        <select
                          className='form-select'
                          value={selectedStore}
                          onChange={e => setSelectedStore(e.target.value)}
                          disabled={loadingStores || submitting}
                        >
                          <option value=''>Select store</option>
                          {stores.map(s => (
                            <option key={s._id ?? s.id} value={s._id ?? s.id}>
                              {s.name ?? s.storeName ?? s._id ?? s.id}
                            </option>
                          ))}
                        </select>
                      </Col>
                      <Col md={4}>
                        <Label className='form-label fw-semibold'>Box Number</Label>
                        <Input
                          value={editedBoxNumber}
                          onChange={e => setEditedBoxNumber(e.target.value)}
                          placeholder='BOX-001'
                          disabled={submitting}
                        />
                      </Col>
                    </Row>
                  )}

                  <div className='table-responsive'>
                    <Table
                      className='so-table table table-sm table-bordered mb-0'
                      responsive
                    >
                      <thead className='table-light'>
                        <tr>
                          <th style={{ width: 50 }}>SI</th>
                          <th>Name</th>
                          <th style={{ width: 150 }}>Part Code</th>
                          <th style={{ width: 90 }}>UOM</th>
                          <th style={{ width: 90 }}>HSN</th>
                          <th style={{ width: 90 }}>QTY</th>
                          <th style={{ width: 100 }}>Sell Price</th>
                          {isEditing && <th style={{ width: 110 }}>Quote Price</th>}
                          <th style={{ width: 110 }}>Discount</th>
                          <th style={{ width: 110 }}>Gst%</th>
                          <th style={{ width: 140 }}>Rate Per Unit</th>
                          <th style={{ width: 140 }}>Order Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.length === 0 ? (
                          <tr>
                            <td colSpan={isEditing ? '11' : '10'} className='text-center py-3'>
                              No line items
                            </td>
                          </tr>
                        ) : (
                          lineItems.map((li, i) => {
                            const lineId = li._id ?? `line-${i}`
                            const editedItem = editedLineItems[lineId]

                            if (isEditing && editedItem) {
                              // Editable row
                              return (
                                <tr key={li._id ?? `${id}-li-${i}`}>
                                  <td>{i + 1}</td>
                                  <td>{li?.model?.modelName}</td>
                                  <td>{li?.model?.partCode}</td>
                                  <td>{li?.model?.uom}</td>
                                  <td>{li?.model?.HSNNumber}</td>
                                  <td>
                                    <Input
                                      type='number'
                                      min='0'
                                      value={editedItem.quantity}
                                      onChange={e => handleEditFieldChange(lineId, 'quantity', e.target.value)}
                                      disabled={submitting}
                                      style={{ width: 80 }}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type='number'
                                      min='0'
                                      step='0.01'
                                      value={editedItem.rate}
                                      onChange={e => handleEditFieldChange(lineId, 'rate', e.target.value)}
                                      disabled={submitting}
                                      style={{ width: 90 }}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type='number'
                                      min='0'
                                      step='0.01'
                                      value={editedItem.quotationPrice}
                                      onChange={e => handleEditFieldChange(lineId, 'quotationPrice', e.target.value)}
                                      disabled={submitting}
                                      style={{ width: 90 }}
                                    />
                                  </td>
                                  <td>
                                    {calculateDiscountFromQuote(editedItem.rate, editedItem.quotationPrice).toFixed(2)}%
                                  </td>
                                  <td>
                                    <select
                                      className='form-select form-select-sm'
                                      value={editedItem.gst}
                                      onChange={e => handleEditFieldChange(lineId, 'gst', e.target.value)}
                                      disabled={submitting}
                                      style={{ width: 70 }}
                                    >
                                      <option value={0.1}>0.1</option>
                                      <option value={5}>5</option>
                                      <option value={12}>12</option>
                                      <option value={18}>18</option>
                                      <option value={28}>28</option>
                                    </select>
                                  </td>
                                  <td>{Number(editedItem.rate || 0).toFixed(2)}</td>
                                  <td>
                                    {(Number(editedItem.rate || 0) * Number(editedItem.quantity || 0)).toFixed(2)}
                                  </td>
                                </tr>
                              )
                            }

                            // Read-only row
                            return (
                              <tr key={li._id ?? `${id}-li-${i}`}>
                                <td>{i + 1}</td>
                                <td>{li?.model?.modelName}</td>
                                <td>{li?.model?.partCode}</td>
                                <td>{li?.model?.uom}</td>
                                <td>{li?.model?.HSNNumber}</td>
                                <td>{li?.quantity}</td>
                                <td>{Number(li?.rate || 0).toFixed(2)}</td>
                                <td>{Number(li?.discount || 0).toFixed(2)}%</td>
                                <td>{li?.gst}%</td>
                                <td>{Number(li?.rate || 0).toFixed(2)}</td>
                                <td>
                                  {Number(li?.rate * li?.quantity || 0).toFixed(
                                    2
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            colSpan={isEditing ? 11 : 10}
                            className='text-end fw-bold'
                          >
                            Total Order Value
                          </td>
                          <td className='fw-bold'>
                            {(
                              lineItems.reduce((acc, li) => {
                                const rate = Number(li?.rate || 0)
                                const qty = Number(li?.quantity || 0)
                                return acc + rate * qty
                              }, 0)
                            ).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>

                  {/* Edit controls for rejected packlists */}
                  {isRejected && !isEditing && (
                    <div className='mb-3 mt-3'>
                      <button
                        className='btn btn-warning'
                        onClick={() => handleStartEdit(pl)}
                      >
                        <i className='bi bi-pencil me-1'></i> Edit & Resubmit
                      </button>
                    </div>
                  )}

                  {/* Action buttons when editing */}
                  {isEditing && (
                    <div className='d-flex justify-content-end gap-2 mt-3'>
                      <Button
                        color='secondary'
                        size='sm'
                        onClick={handleCancelEdit}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        color='primary'
                        size='sm'
                        onClick={() => handleResubmit(plId)}
                        disabled={submitting}
                      >
                        {submitting ? 'Resubmitting...' : 'Resubmit Pack List'}
                      </Button>
                    </div>
                  )}
                </AccordionBody>
              </AccordionItem>
            )
          })}
        </Accordion>
      </CardBody>
    </div>
  )
}

export default ViewPackList
